import { useMemo } from 'react';

export type UseAugmentQAProps<T> = T & {
  /** `qa` will be rendered as `data-qa` */
  qa: string;
};

/**
 * Hook to help with augmenting an element with custom QA attributes
 *
 * Returns props that should be spread into the target element
 *
 * This variant is useful when dealing with lists of elements.
 * For a single item see `useAugmentQA()`
 */
export function useAugmentQAList<T = unknown>(items: UseAugmentQAProps<T>[]) {
  return useMemo(
    () =>
      items.map(({ qa, ...props }) => {
        return { ...props, 'data-qa': qa };
      }),
    [items]
  );
}

/**
 * Hook to help with augmenting an element with custom QA attributes
 *
 * Returns props that should be spread into the target element
 *
 * This variant is useful when dealing with a single element.
 * For multiple items see `useAugmentQAList()`
 */
export function useAugmentQA<T = unknown>(item: UseAugmentQAProps<T>) {
  return useAugmentQAList(useMemo(() => [item], [item]))[0];
}
